import React from "react";
import { Link } from "react-router-dom";
import { Bookmark, Gift, ShieldCheck, Star, UserMinus, UserPlus } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { ProfileMilestoneBadges } from "./ProfileMilestoneBadges";
import { ProBadge } from "../../components/shared/ProBadge";

/**
 * ProfileCreatorInfoBar — the horizontal "avatar · stats · follow ·
 * gift · tier · milestones" strip under the TV. Pure presentational;
 * parent owns follow state and the click handler.
 *
 * Props:
 *   profile         — full profile doc (username, bio, profile_image,
 *                     videos.length, total_views, total_applauds,
 *                     subscription_tier, twofa_enabled, milestone_badges)
 *   followersCount  — int, kept in sync with the /follow API response
 *   following       — bool, am I currently following this user
 *   followLoading   — bool, follow API in flight
 *   onFollow        — () => void  toggles follow/unfollow
 *   isOwnProfile    — bool, hides Follow/Gift on own profile
 *   user            — current viewer (null when logged out)
 */
export const ProfileCreatorInfoBar = ({
  profile, followersCount, following, followLoading, onFollow, isOwnProfile, user,
}) => (
  // Jun 9, 2026 — `mr-20 sm:mr-0` reserves a right-edge gutter on
  // mobile so the floating Record FAB cluster can't chop the
  // "Secured" verification badge / Follow button. Same pattern as
  // FoundingBadge.
  <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 py-4 border-t border-b border-gray-800/50 mr-20 sm:mr-0">
    {/* Avatar & Name */}
    <div className="flex items-center gap-3">
      <Avatar className="h-10 w-10 border-2 border-emerald-500/50">
        <AvatarImage src={profile.profile_image} />
        <AvatarFallback className="bg-emerald-600 text-sm">{profile.username[0].toUpperCase()}</AvatarFallback>
      </Avatar>
      <div>
        <div className="flex items-center gap-1.5">
          <p className="text-white font-medium text-sm">{profile.username}</p>
          {profile.twofa_enabled && (
            <span
              className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[10px] font-medium leading-none"
              title="This account is protected by two-factor authentication."
              data-testid="profile-2fa-badge"
            >
              <ShieldCheck className="h-3 w-3" /> Secured
            </span>
          )}
        </div>
        <p className="text-gray-500 text-xs">{profile.bio || "Creator"}</p>
      </div>
    </div>

    {/* Compact Stats — Jun 11, 2026: shrunk on mobile (text-[10px] +
        gap-2) so labels like "Subscribers" don't run behind the Emergent
        platform badge on iPhone-width viewports. Desktop unchanged. */}
    <div className="flex items-center gap-2 sm:gap-6 text-sm">
      <div className="text-center">
        <p className="font-bold text-white text-xs sm:text-sm">{followersCount}</p>
        <p className="text-gray-500 text-[10px] sm:text-xs">Subscribers</p>
      </div>
      <div className="text-center">
        <p className="font-bold text-white text-xs sm:text-sm">{profile.videos.length}</p>
        <p className="text-gray-500 text-[10px] sm:text-xs">Videos</p>
      </div>
      <div className="text-center">
        <p className="font-bold text-white text-xs sm:text-sm">{profile.total_views}</p>
        <p className="text-gray-500 text-[10px] sm:text-xs">Views</p>
      </div>
      <div className="text-center">
        <p className="font-bold text-pink-400 text-xs sm:text-sm">{profile.total_applauds}</p>
        <p className="text-gray-500 text-[10px] sm:text-xs">Applauds</p>
      </div>
      {/* Saves — sum of viewers' Trail bookmarks across this creator's
          videos. Only rendered when ≥1 so a fresh creator's strip stays
          tight. Complements the producer-facing "Saved" stat on Dashboard
          by exposing the same number to discovery surfaces. */}
      {(profile.total_saves || 0) > 0 && (
        <div className="text-center" data-testid="profile-total-saves">
          <p className="font-bold text-amber-400 flex items-center justify-center gap-1 text-xs sm:text-sm">
            <Bookmark className="h-3 w-3" />
            {profile.total_saves}
          </p>
          <p className="text-gray-500 text-[10px] sm:text-xs">Saves</p>
        </div>
      )}
    </div>

    {/* Follow Button */}
    {!isOwnProfile && user && (
      <Button
        onClick={onFollow}
        disabled={followLoading}
        size="sm"
        className={following ? "bg-gray-800 border border-gray-600 text-gray-300 hover:bg-gray-700" : "bg-emerald-600 hover:bg-emerald-700"}
        data-testid="follow-btn"
      >
        {followLoading ? "..." : (
          following ? (
            <><UserMinus className="h-4 w-4 mr-1" /> Unfollow</>
          ) : (
            <><UserPlus className="h-4 w-4 mr-1" /> Subscribe</>
          )
        )}
      </Button>
    )}

    {/* Gift this creator */}
    {!isOwnProfile && user && (
      <Link to={`/gifts?to=${profile.username}`} data-testid="profile-gift-btn">
        <Button
          size="sm"
          className="bg-pink-600 hover:bg-pink-700 text-white"
          title={`Send @${profile.username} 100 credits ($4 mocked)`}
        >
          <Gift className="h-4 w-4 mr-1" /> Gift
        </Button>
      </Link>
    )}

    {/* Tier Badge — Producer gets the unified gold ProBadge; other tiers
        keep the emerald Star badge. Iter-79: consistent visual language
        with Recorder/Profile/Pricing so viewers recognise Producer status. */}
    {(profile.subscription_tier || "").toLowerCase() === "producer" ? (
      <ProBadge unlocked label="Producer" testId="creator-tier-pro-badge" />
    ) : (
      <Badge className="bg-emerald-600/20 border border-emerald-500/30 text-emerald-400 text-xs">
        <Star className="h-3 w-3 mr-1" /> {profile.subscription_tier.charAt(0).toUpperCase() + profile.subscription_tier.slice(1)}
      </Badge>
    )}

    <ProfileMilestoneBadges badges={profile.milestone_badges} />
  </div>
);
